import moment from "moment";

import { FormFieldRow } from "@/Common/Components/Forms/FormFieldRow";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { InlineOption, InlineOptionsListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { SelectKVPControl } from "@/Common/Components/Inputs/SelectKVPControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { IMap } from "@/Common/Models/IMap";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { ConditionCategory } from "../Constants/ConditionCategory";
import { ConstantValues } from "../Constants/ConstantValues";
import { ValueSetType } from "../Constants/ValueSetType";
import { CodeValue } from "../Models/CodeValue";
import { ConditionModel } from "../Models/ConditionModel";
import { LocationModel } from "../Models/LocationModel";
import { RvqDomainValue } from "../Models/RvqDomainValue";
import { QuebecVaccinationRegistryHelper } from "../QuebecVaccinationRegistryHelper";
import { GetValuesRequest } from "../RequestModels/GetValuesRequest";
import { RvqLocationSelection } from "../RvqLocationSelection";
import { BaseImmunizationProfileSection, BaseImmunizationProfileSectionState, IBaseImmunizationProfileSectionProps } from "./BaseImmunizationProfileSection";

interface IAntigenCommonComponent extends IBaseImmunizationProfileSectionProps {
    conditionModel: ConditionModel;
    category: ConditionCategory;
}

class AntigenCommonComponentState extends BaseImmunizationProfileSectionState {
    public selectedAntigen: string;
    public selectedReason: string;
    public motive: string;
    public attestationSource: string;
    public vaccinationLocation: LocationModel;

    public antigenOptions: KeyValuePair<string, string>[];
    public immunizingAgentsOptions: KeyValuePair<string, string>[];
    public attestationSourceOptions: IMap<RvqDomainValue>;
    public reasonOptions: IMap<RvqDomainValue>;

    public conditionModel: ConditionModel;

    public selectedAntigenOrImmunizingAgent: string;
    public isAntigenSelected: boolean;

    constructor(init?: Partial<AntigenCommonComponentState>) {
        super(init);
    }

    public static default(): AntigenCommonComponentState {
        return new AntigenCommonComponentState({
            ...BaseImmunizationProfileSectionState.default(),
            selectedAntigen: null,
            antigenOptions: [],
            attestationSource: null,
            attestationSourceOptions: {},
            vaccinationLocation: null,
            conditionModel: null,
            reasonOptions: {},
            immunizingAgentsOptions: [],
            isAntigenSelected: true,
            selectedAntigenOrImmunizingAgent: null
        });
    }
}

class AntigenCommonComponent extends BaseImmunizationProfileSection<AntigenCommonComponentState, IAntigenCommonComponent> {
    public constructor(props: IAntigenCommonComponent) {
        super(props);

        const initialState = AntigenCommonComponentState.default();
        initialState.conditionModel = new ConditionModel(props.conditionModel);
        initialState.conditionModel.Category.Code = EnumHelper.getName(ConditionCategory, props.category);
        initialState.conditionModel.PatientId = props.patientId;

        this.state = initialState;
    }

    protected requestValues = new GetValuesRequest({
        Type: [
            ValueSetType.Antigen,
            ValueSetType.Agent,
            ValueSetType.EvidenceSource,
            ValueSetType.ContraindicationReason
        ]
    });

    public async componentDidMount(): Promise<void> {
        super.componentDidMount(
            () => this.mutateState(s => {
                s.antigenOptions = KeyValuePair.fromMapOfStrings(s.values?.Types[ValueSetType.Antigen.toString()]);
                s.attestationSourceOptions = s.values?.Types[ValueSetType.EvidenceSource.toString()];
                s.reasonOptions = s.values.Types[ValueSetType.ContraindicationReason.toString()];
                s.immunizingAgentsOptions = KeyValuePair.fromMapOfStrings(s.values.Types[ValueSetType.Agent.toString()]);
                s.isAntigenSelected = this.isNew || !!s.conditionModel?.Antigen?.Label;
                s.selectedAntigenOrImmunizingAgent = !this.isNew
                    ? (
                        s.isAntigenSelected
                            ? s.conditionModel?.Antigen.Label
                            : s.conditionModel?.Agent.Label
                    )
                    : null;

                s.attestationSource = !this.isNew ? s.conditionModel.Evidence[0]?.Code : null;

                s.selectedReason = !this.isNew ? s.conditionModel.Reason.Code : null;

                s.motive = !this.isNew ? s.conditionModel.ReasonDetails : null;
            })
        );

        if (this.state.conditionModel?.LocationId && !this.isNew) {
            const vaccinationLocation = await this.getLocationById(this.state.conditionModel.LocationId);
            this.mutateState(s => {
                if (vaccinationLocation)
                    s.vaccinationLocation = vaccinationLocation;
            });
        }
        else if (this.isNew) {
            const defaultLocation = QuebecVaccinationRegistryHelper.getDefaultPreference(this.props.locationPreferences);
            if (defaultLocation) {
                this.mutateState(
                    s => s.vaccinationLocation = new LocationModel({
                        DisplayText: defaultLocation.DisplayName,
                        Id: defaultLocation.QuebecVaccinationRegistryId
                    })
                );
            }
        }
    }

    protected getBody(): JSX.Element {
        return (
            <div>
                {this.getAntigenRender()}
                {this.renderReasonAndMotive()}
                <div className="row margin-sm-bottom">
                    <div className="col-md-12">
                        <label className="bold">{cacheStore.GetString("QuebecVaccinationRegistry_ApplicationDateLabel")}</label>
                        <div>
                            <FormFieldRow
                                isEditMode={this.isNew}
                                value={this.formatDateForMoment(this.state.conditionModel.DatePeriod.Start)}
                                labelValueKey="DuLabel"
                                style={{ width: "260px", display: "inline-block" }}
                                inline
                                disableBoldLabel
                                valueStyle={{ marginTop: "10px" }}
                            >
                                <DateSelector
                                    id="immunityProofFromDate"
                                    date={this.state.conditionModel.DatePeriod.Start}
                                    dateFormat={this.dateFormatForDateControl}
                                    onChangeCallback={date => this.mutateState(s => s.conditionModel.DatePeriod.Start = date)}
                                    valid={this.isStartDateValid()}
                                />
                            </FormFieldRow>
                            <FormFieldRow
                                isEditMode={!this.isViewing}
                                value={this.formatDateForMoment(this.state.conditionModel.DatePeriod.End)}
                                labelValueKey="AuLabel"
                                style={{ marginLeft: "10px", width: "260px", display: "inline-block" }}
                                inline
                                disableBoldLabel
                                valueStyle={{ marginTop: "10px" }}
                            >
                                <DateSelector
                                    id="immunityProofToDate"
                                    date={this.state.conditionModel.DatePeriod.End}
                                    dateFormat={this.dateFormatForDateControl}
                                    valid={this.isEndDateValid()}
                                    onChangeCallback={date => this.mutateState(s => s.conditionModel.DatePeriod.End = date)}
                                />
                            </FormFieldRow>
                        </div>
                    </div>
                </div>
                <FormFieldRow
                    labelValueKey="QuebecVaccinationRegistry_SourceOfAttestationLabel"
                    isEditMode={this.isNew}
                    value={this.attestationSourceValue}
                >
                    <SelectKVPControl
                        Callback={val => this.mutateState(s => s.attestationSource = val)}
                        AvailableValues={this.attestationSourceOptions}
                        isInvalid={!this.state.attestationSource}
                        Value={this.state.attestationSource}
                        IncludeEmptyChoice
                    />
                </FormFieldRow>
                <FormFieldRow
                    labelValueKey="QuebecVaccinationRegistry_ImmunizationLocationLabel"
                    isEditMode={this.isNew}
                    value={LocationModel.getDisplayName(this.state.vaccinationLocation)}
                >
                    <RvqLocationSelection
                        selectedItem={this.state.vaccinationLocation?.Id
                            ? {
                                Id: this.state.vaccinationLocation.Id,
                                DisplayText: LocationModel.getDisplayName(this.state.vaccinationLocation)
                            }
                            : null
                        }
                        preferences={this.props.locationPreferences}
                        onChange={(id, displayText, newPreferences) => {
                            this.mutateState(
                                s => {
                                    s.vaccinationLocation = new LocationModel({
                                        Id: id,
                                        DisplayText: displayText
                                    });
                                },
                                () => newPreferences && this.props.updatePreferences(null, null, newPreferences)
                            );
                        }}
                        onPreferencesUpdate={(preferences, then) => this.props.updatePreferences(null, null, preferences, then)}
                        onClear={() => this.mutateState(s => s.vaccinationLocation = null)}
                        isValid={!!this.state.vaccinationLocation}
                        isRvqAuthenticated={true}
                    />
                </FormFieldRow>
                <FormFieldRow
                    labelValueKey="CommentsLabel"
                    isEditMode={this.isNew}
                    value={JSXHelper.BreakStringIntoParagraphs(this.state.conditionModel.Notes)}
                >
                    <textarea
                        id="immunityProofComments"
                        onChange={(e: any) => {
                            const value = e.target.value;
                            this.mutateState(s => s.conditionModel.Notes = value);
                        }}
                        value={this.state.conditionModel.Notes}
                        className={"form-control textarea-resize-vertical " + (IsNullOrWhiteSpace(this.state.conditionModel.Notes) ? "invalid" : "")}
                        rows={3}
                    />
                </FormFieldRow>
            </div>
        );
    }

    private get attestationSourceValue(): string {
        return QuebecVaccinationRegistryHelper.translateValueSet(this.state.attestationSource, this.state.attestationSourceOptions[this.state.attestationSource]?.Label, ValueSetType.EvidenceSource);
    }

    private get selectedReasonValue(): string {
        return QuebecVaccinationRegistryHelper.translateValueSet(this.state.selectedReason, this.state.reasonOptions[this.state.selectedReason]?.Label, ValueSetType.ContraindicationReason);
    }

    private get attestationSourceOptions(): KeyValuePair<string, string>[] {
        return KeyValuePair.fromMapOfStrings(QuebecVaccinationRegistryHelper.getTranslatedDictionary(this.state.attestationSourceOptions, ValueSetType.EvidenceSource));
    }

    private isEndDateValid(): boolean {
        const startDate = DateHelper.parseOptional(this.state.conditionModel.DatePeriod?.Start);
        const endDate = DateHelper.parseOptional(this.state.conditionModel.DatePeriod?.End);
        const today = moment().endOf("day");

        if (!endDate)
            return true;

        return endDate.isAfter(today)
            && (!startDate || endDate.isAfter(startDate));
    }

    private isStartDateValid(): boolean {
        const startDate = DateHelper.parseOptional(this.state.conditionModel.DatePeriod?.Start);
        const endDate = DateHelper.parseOptional(this.state.conditionModel.DatePeriod?.End);
        const today = moment().endOf("day");

        return !!startDate
            && startDate.isBefore(today)
            && (!endDate || startDate.isBefore(endDate));
    }

    private renderReasonAndMotive(): JSX.Element {
        let invalidBorder = { border: "1px solid #a94442" };
        let isMotiveNeeded = this.state.selectedReason === ConstantValues.OtherReasonCode;

        if (this.props.category !== ConditionCategory.contraindicationPrecaution)
            return null;

        return (
            <div>
                <FormFieldRow
                    labelValueKey="ReasonLabel"
                    isEditMode={this.isNew}
                    value={this.selectedReasonValue}
                >
                    <SelectKVPControl
                        Callback={val => this.mutateState(s => s.selectedReason = val)}
                        AvailableValues={this.reasonOptions}
                        Value={this.state.selectedReason}
                        IncludeEmptyChoice
                        isInvalid={!this.state.selectedReason}
                    />
                </FormFieldRow>
                {this.state.selectedReason === ConstantValues.OtherReasonCode && (
                    <FormFieldRow
                        labelValueKey="QuebecVaccinationRegistry_MotiveLabel"
                        isEditMode={this.isNew}
                        value={this.state.motive}
                    >
                        <TextboxControl
                            onChange={val => this.mutateState(s => s.motive = val)}
                            value={this.state.motive}
                            style={isMotiveNeeded && IsNullOrWhiteSpace(this.state.motive) ? invalidBorder : {}}
                            disabled={!isMotiveNeeded}
                        />
                    </FormFieldRow>
                )}
            </div>
        );
    }

    private get reasonOptions(): KeyValuePair<string, string>[] {
        return KeyValuePair.fromMapOfStrings(QuebecVaccinationRegistryHelper.getTranslatedDictionary(this.state.reasonOptions, ValueSetType.ContraindicationReason));
    }

    private getAntigenRender(): JSX.Element {
        switch (this.props.category) {
            case ConditionCategory.contraindicationPrecaution:
                return this.antigenSelection();

            case ConditionCategory.immunizationProof:
                return this.baseAntigen(this.state.antigenOptions);
        }
    }

    private antigenSelection(): JSX.Element {
        return (
            <div>
                <FormFieldRow
                    isEditMode={this.isNew}
                    value={this.state.selectedAntigenOrImmunizingAgent}
                >
                    <InlineOptionsListControl
                        id="isAntigenOrNot"
                        items={[
                            new InlineOption(true, cacheStore.GetString("QuebecVaccinationRegistry_AntigenLabel")),
                            new InlineOption(false, cacheStore.GetString("QuebecVaccinationRegistry_ImmunizingAgentLabel"))
                        ]}
                        value={this.state.isAntigenSelected}
                        onChange={option => this.mutateState(s => {
                            s.isAntigenSelected = option.Key;
                            s.selectedAntigenOrImmunizingAgent = null;
                        })}
                        disabled={!this.isNew}
                        boldLabels
                    />
                    {this.baseAntigen(this.state.isAntigenSelected ? this.state.antigenOptions : this.state.immunizingAgentsOptions)}
                </FormFieldRow>
            </div>
        );
    }

    private baseAntigen(values: KeyValuePair<string, string>[]): JSX.Element {
        return (
            <div>
                <FormFieldRow
                    labelValueKey={this.props.category === ConditionCategory.immunizationProof ? "QuebecVaccinationRegistry_AntigenLabel" : ""}
                    isEditMode={this.isNew}
                    value={this.state.selectedAntigenOrImmunizingAgent}
                >
                    <SelectKVPControl
                        Callback={val => this.mutateState(s => {
                            s.selectedAntigenOrImmunizingAgent = val;
                        })}
                        IncludeEmptyChoice
                        AvailableValues={values}
                        isInvalid={!this.state.selectedAntigenOrImmunizingAgent}
                        Value={this.state.selectedAntigenOrImmunizingAgent}
                    />
                </FormFieldRow>
            </div>
        );
    }

    protected canSave(): boolean {
        const startDate = DateHelper.parseOptional(this.state.conditionModel.DatePeriod.Start);
        const endDate = DateHelper.parseOptional(this.state.conditionModel.DatePeriod.End);

        return startDate
            && this.state.selectedAntigenOrImmunizingAgent
            && this.state.attestationSource
            && this.state.conditionModel.Notes
            && !!this.state.vaccinationLocation
            && this.isStartDateValid()
            && (!endDate || (startDate.isBefore(endDate) && endDate.isAfter(moment().endOf("day"))))
            && (!this.isEditing || !!endDate)
            && this.isOtherReasonValid();
    }

    private isOtherReasonValid(): boolean {
        return this.props.category !== ConditionCategory.contraindicationPrecaution
            || this.state.selectedReason !== ConstantValues.OtherReasonCode
            || !IsNullOrWhiteSpace(this.state.motive);
    }

    protected async onSave(): Promise<void> {
        this.saveConditionModel(this.isNew
            ? this.convertToConditionModel()
            : this.state.conditionModel
        );
    }

    private convertToConditionModel(): ConditionModel {
        let condition = this.state.conditionModel;

        this.state.isAntigenSelected
            ? condition.Antigen = this.getCodeValue(ValueSetType.Antigen, this.state.selectedAntigenOrImmunizingAgent)
            : condition.Agent = this.getCodeValue(ValueSetType.Agent, this.state.selectedAntigenOrImmunizingAgent);

        condition.Reason = (this.props.category === ConditionCategory.immunizationProof)
            ? new CodeValue({
                Code: ConstantValues.ConditionReason_ProofOfImmunity
            })
            : this.getCodeValue(ValueSetType.ContraindicationReason, this.state.selectedReason);

        condition.ReasonDetails = this.state.motive;

        if (this.state.attestationSource)
            condition.Evidence.push(this.getCodeValue(ValueSetType.EvidenceSource, this.state.attestationSource));

        condition.LocationId = this.state.vaccinationLocation?.Id;

        return condition;
    }
}

export { AntigenCommonComponent };
